import React, { useState } from "react";
import { auth, googleProvider } from "../firebase";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { FcGoogle } from "react-icons/fc";
import companyLogo from "../Assets/logo3.jpg";
import axiosInstance from "../config/axios";
import { setAppSession, setFirebaseLoginAt } from "../utils/appAuth";

const ROLES = [
  { id: "admin", label: "Admin", hint: "Full office system" },
  { id: "superuser", label: "Super Users", hint: "Review agent drafts" },
  { id: "broker", label: "Broker", hint: "Listings, trades, and office overview" },
  { id: "agent", label: "Agents", hint: "Your listings and trades" },
  {
    id: "finance",
    label: "Finance",
    hint: "Income statement, balance sheet, journals, A/P, A/R, bank recon, HST",
  },
];

const Login = ({ setUser, setAppSessionState }) => {
  const [selectedRole, setSelectedRole] = useState("admin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLogin, setIsLogin] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const clearFields = () => {
    setEmail("");
    setPassword("");
  };

  const login = async () => {
    try {
      setIsSubmitting(true);
      setErrorMsg("");
      if (selectedRole !== "admin") {
        await signOut(auth).catch(() => {});
        const res = await axiosInstance.post("/auth/login", {
          username: email,
          password,
          role: selectedRole,
        });
        const session = {
          token: res.data.token,
          user: res.data.user,
          loginAt: Date.now(),
        };
        setAppSession(session);
        setAppSessionState(session);
        setUser(null);
        return;
      }
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      setAppSession(null);
      setAppSessionState(null);
      setFirebaseLoginAt(Date.now());
      setUser(userCredential.user);
    } catch (error) {
      if (error.response?.data?.message) {
        setErrorMsg(error.response.data.message);
      } else if (error.code === "auth/user-not-found") {
        setErrorMsg("User not found. Please sign up first.");
      } else if (error.code === "auth/wrong-password") {
        setErrorMsg("Incorrect password. Please try again.");
      } else {
        setErrorMsg("Login failed. Please check your credentials.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const signUp = async () => {
    try {
      setIsSubmitting(true);
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      setAppSession(null);
      setAppSessionState(null);
      setFirebaseLoginAt(Date.now());
      setUser(userCredential.user);
      setErrorMsg("");
    } catch (error) {
      if (error.code === "auth/email-already-in-use") {
        setErrorMsg("Email already in use. Please login instead.");
      } else if (error.code === "auth/weak-password") {
        setErrorMsg("Password too weak. Use at least 6 characters.");
      } else {
        setErrorMsg("Sign up failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      setAppSession(null);
      setAppSessionState(null);
      setFirebaseLoginAt(Date.now());
      setUser(userCredential.user);
      setErrorMsg("");
    } catch {
      setErrorMsg("Error logging in with Google.");
    }
  };

  const requestPassword = async () => {
    if (!email.trim()) {
      setSuccessMsg("");
      setErrorMsg("Enter your username or email first.");
      return;
    }
    try {
      setIsSubmitting(true);
      setErrorMsg("");
      setSuccessMsg("");
      const res = await axiosInstance.post("/auth/forgot-password", {
        username: email,
        role: selectedRole,
      });
      setSuccessMsg(
        res.data.message ||
          "The administrator has been notified and will email a new password."
      );
    } catch (error) {
      setErrorMsg(
        error.response?.data?.message ||
          "Unable to submit that request. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-shell min-h-screen flex items-center justify-center p-4">
      <div className="login-card bg-white p-8 rounded-lg shadow-lg w-full max-w-md text-center">
        <img src={companyLogo} alt="Logo" className="h-16 mx-auto mb-6" />
        <h2 className="text-3xl font-semibold mb-2 text-gray-900">
          {showForgotPassword && selectedRole !== "admin"
            ? "Forgot password"
            : isLogin || selectedRole !== "admin"
              ? "Login"
              : "Sign Up"}
        </h2>
        <p className="text-sm text-gray-500 mb-6">Select your user type</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-6">
          {ROLES.map((role) => (
            <button
              key={role.id}
              type="button"
              onClick={() => {
                setSelectedRole(role.id);
                setErrorMsg("");
                setSuccessMsg("");
                setIsLogin(true);
                setShowForgotPassword(false);
              }}
              className={`rounded-md border px-2 py-3 text-sm font-semibold transition ${
                selectedRole === role.id
                  ? "bg-blue-900 text-white border-blue-900"
                  : "bg-white text-gray-700 border-gray-300 hover:border-blue-900"
              }`}
            >
              {role.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mb-4">
          {ROLES.find((r) => r.id === selectedRole)?.hint}
        </p>

        {errorMsg && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
            {errorMsg}
          </div>
        )}
        {successMsg && (
          <div className="mb-4 p-3 bg-green-100 text-green-800 rounded">
            {successMsg}
          </div>
        )}

        <label className="block text-left font-bold mb-1">
          {selectedRole === "admin" ? "Email" : "Username"}
        </label>
        <input
          type={selectedRole === "admin" ? "email" : "text"}
          placeholder={
            selectedRole === "admin" ? "Enter email" : "Enter username or email"
          }
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            if (showForgotPassword && selectedRole !== "admin") {
              requestPassword();
            } else {
              login();
            }
          }}
          className="w-full p-2 mb-4 border rounded focus:outline-yellow-400"
        />

        {showForgotPassword && selectedRole !== "admin" ? (
          <>
            <p className="text-sm text-gray-600 text-left mb-4">
              Enter your username or email. The administrator will be notified
              and can email you a new password.
            </p>
            <button
              onClick={requestPassword}
              disabled={isSubmitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded font-semibold mb-4 transition disabled:opacity-60"
            >
              {isSubmitting ? "Sending request..." : "Request new password"}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setErrorMsg("");
                setSuccessMsg("");
              }}
              className="text-sm text-blue-900 font-semibold hover:underline"
            >
              Back to login
            </button>
          </>
        ) : (
          <>
        <label className="block text-left font-bold mb-1">Password</label>
        <input
          type="password"
          placeholder="Enter password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full p-2 mb-2 border rounded focus:outline-yellow-400"
        />
        {selectedRole !== "admin" && (
          <div className="text-right mb-4">
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(true);
                setErrorMsg("");
                setSuccessMsg("");
              }}
              className="text-sm text-blue-900 font-semibold hover:underline"
            >
              Forgot password?
            </button>
          </div>
        )}

        {selectedRole !== "admin" ? (
          <button
            onClick={login}
            disabled={isSubmitting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded font-semibold mb-4 transition disabled:opacity-60"
          >
            {isSubmitting ? "Signing in..." : "Login"}
          </button>
        ) : isLogin ? (
          <>
            <button
              onClick={login}
              disabled={isSubmitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded font-semibold mb-4 transition disabled:opacity-60"
            >
              {isSubmitting ? "Signing in..." : "Login"}
            </button>

            <button
              onClick={loginWithGoogle}
              className="w-full flex items-center justify-center gap-2 border border-gray-300 py-2 rounded hover:bg-gray-100 transition mb-4"
            >
              <FcGoogle size={22} />
              Login with Google
            </button>

            <p className="text-sm text-gray-700">
              Don't have an account?{" "}
              <button
                onClick={() => {
                  setIsLogin(false);
                  setErrorMsg("");
                  clearFields();
                }}
                className="text-blue-900 font-semibold hover:underline"
              >
                Sign Up
              </button>
            </p>
          </>
        ) : (
          <>
            <button
              onClick={signUp}
              disabled={isSubmitting}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded font-semibold mb-4 transition disabled:opacity-60"
            >
              Sign Up
            </button>

            <button
              onClick={loginWithGoogle}
              className="w-full flex items-center justify-center gap-2 border border-gray-300 py-2 rounded hover:bg-gray-100 transition mb-4"
            >
              <FcGoogle size={22} />
              Sign Up with Google
            </button>

            <p className="text-sm text-gray-700">
              Already have an account?{" "}
              <button
                onClick={() => {
                  setIsLogin(true);
                  setErrorMsg("");
                  clearFields();
                }}
                className="text-blue-900 font-semibold hover:underline"
              >
                Login
              </button>
            </p>
          </>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
